// ---------------------------------------------------------------------------
// attach-resizable — drag a card's edge handles to resize by whole grid cells
// ---------------------------------------------------------------------------
//
// Uses PointerEvents (NOT HTML5 drag) so it can't collide with the HTML5-drag
// reorder on the same card. Edge handles are NOT draggable; pointerdown on one
// captures the pointer and snaps colSpan (right handle) / rowSpan (bottom handle)
// per cell as the pointer moves. Owns no order/size state — it reports each snap
// via onResize; the controller mutates + persists (debounced).
//
// Geometry reads are behind an injectable `measureGrid()` seam so the snap path
// is testable under jsdom (which lacks layout): tests pass a stub returning real
// cell sizes; the default reads getComputedStyle and returns zeros in jsdom,
// where the handler then no-ops.

import { computeSpan } from './compute-span.js';

/** @param {HTMLElement} grid @returns {{colCellPx:number,rowCellPx:number,colGapPx:number,rowGapPx:number}} */
function defaultMeasureGrid(grid) {
  if (typeof getComputedStyle !== 'function') {
    return { colCellPx: 0, rowCellPx: 0, colGapPx: 0, rowGapPx: 0 };
  }
  const cs = getComputedStyle(grid);
  const cols = (cs.gridTemplateColumns || '').split(' ').filter(Boolean).length;
  const colGapPx = parseFloat(cs.columnGap) || 0;
  const rowGapPx = parseFloat(cs.rowGap) || 0;
  const width = grid.clientWidth || 0;
  const colCellPx = cols > 0 ? (width - colGapPx * (cols - 1)) / cols : 0;
  const rowCellPx = parseFloat(cs.gridAutoRows) || 0;
  return { colCellPx, rowCellPx, colGapPx, rowGapPx };
}

/**
 * @param {HTMLElement} container
 * @param {{
 *   itemSelector: string,
 *   keyAttr: string,
 *   colHandleSelector: string,
 *   rowHandleSelector: string,
 *   getSpans: (id: string) => { colSpan: number, rowSpan: number, colMax: number, rowMax: number },
 *   onResize: (id: string, size: { colSpan: number, rowSpan: number }) => void,
 *   isBlocked?: () => boolean,
 *   measureGrid?: (grid: HTMLElement) => { colCellPx: number, rowCellPx: number, colGapPx: number, rowGapPx: number },
 * }} options
 * @returns {{ destroy: () => void }}
 */
export function attachResizable(container, options) {
  const {
    itemSelector, keyAttr, colHandleSelector, rowHandleSelector,
    getSpans, onResize, isBlocked,
  } = options;
  const measureGrid = options.measureGrid || defaultMeasureGrid;

  /** @type {null | {
   *   id: string, axis: 'col'|'row', startX: number, startY: number,
   *   startSpan: number, cellPx: number, gapPx: number, min: number, max: number,
   *   last: number, pointerId: number, handle: HTMLElement,
   * }} */
  let drag = null;

  /** @param {EventTarget|null} t @param {string} sel @returns {HTMLElement|null} */
  const closestOf = (t, sel) => (t instanceof HTMLElement ? /** @type {HTMLElement|null} */ (t.closest(sel)) : null);

  /** @param {PointerEvent} e */
  function onPointerDown(e) {
    if (isBlocked?.()) return;
    if (drag) return; // a resize is already in flight — a second pointer can't take over
    const colHandle = closestOf(e.target, colHandleSelector);
    const rowHandle = closestOf(e.target, rowHandleSelector);
    const handle = colHandle || rowHandle;
    if (!handle) return;
    const item = closestOf(e.target, itemSelector);
    if (!item) return;
    const id = item.getAttribute(keyAttr);
    if (!id) return;

    const geo = measureGrid(container);
    const axis = colHandle ? 'col' : 'row';
    const cellPx = axis === 'col' ? geo.colCellPx : geo.rowCellPx;
    const gapPx = axis === 'col' ? geo.colGapPx : geo.rowGapPx;
    if (!cellPx) return; // no geometry (jsdom / not laid out) → no-op

    const spans = getSpans(id);
    const startSpan = axis === 'col' ? spans.colSpan : spans.rowSpan;
    const breakpointMax = axis === 'col' ? spans.colMax : spans.rowMax;
    // Effective max captured ONCE for the whole gesture: an over-max start can
    // step down and back up within this drag, but a fresh gesture re-clamps.
    const max = Math.max(startSpan, breakpointMax);

    e.preventDefault();
    e.stopPropagation();
    drag = {
      id, axis, startX: e.clientX, startY: e.clientY, startSpan,
      cellPx, gapPx, min: 1, max, last: startSpan, pointerId: e.pointerId, handle,
    };
    try { handle.setPointerCapture(e.pointerId); } catch { /* jsdom / unsupported */ }
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);
    handle.addEventListener('lostpointercapture', onPointerUp);
  }

  /** @param {PointerEvent} e */
  function onPointerMove(e) {
    if (!drag || e.pointerId !== drag.pointerId) return; // ignore other pointers
    const deltaPx = drag.axis === 'col' ? e.clientX - drag.startX : e.clientY - drag.startY;
    const next = computeSpan(drag.startSpan, deltaPx, drag.cellPx, drag.gapPx, drag.min, drag.max);
    if (next === drag.last) return;
    drag.last = next;
    const spans = getSpans(drag.id);
    onResize(drag.id, drag.axis === 'col'
      ? { colSpan: next, rowSpan: spans.rowSpan }
      : { colSpan: spans.colSpan, rowSpan: next });
  }

  /** @param {PointerEvent} [e] */
  function onPointerUp(e) {
    if (!drag) return;
    // Only the active pointer terminates the gesture — including lostpointercapture
    // (a real capture-loss for our pointer carries our pointerId).
    if (e && e.pointerId !== drag.pointerId) return;
    endDrag();
  }

  function endDrag() {
    if (!drag) return;
    const { handle, pointerId } = drag;
    try { handle.releasePointerCapture(pointerId); } catch { /* ignore */ }
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', onPointerUp);
    handle.removeEventListener('lostpointercapture', onPointerUp);
    drag = null;
  }

  container.addEventListener('pointerdown', onPointerDown);

  return {
    destroy() {
      container.removeEventListener('pointerdown', onPointerDown);
      // Tear down any in-flight drag (document + handle listeners, capture).
      endDrag();
      drag = null;
    },
  };
}
